package com.tovtalk.presentation.viewmodel.hashtag

import com.tovtalk.presentation.ViewModel
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.HashtagRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.domain.entity.DomainResult
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch

class HashtagViewModel(
    private val hashtagRepository: HashtagRepository,
    private val hashtag: String,
) : ViewModel() {

    private val _posts = MutableStateFlow<UiState<List<Post>>>(UiState.Loading)
    val posts: StateFlow<UiState<List<Post>>> = _posts

    private var nextCursor: String? = null
    private var isLoadingMore = false
    // Bumped on every refresh so an in-flight loadMore() from a previous load can't
    // append its stale page onto a freshly-reloaded list. (Tag is fixed, so no
    // cross-query race — only refresh-vs-loadMore needs guarding.)
    private var loadGeneration = 0

    init {
        loadPosts()
    }

    fun loadPosts() {
        ++loadGeneration
        viewModelScope.launch {
            _posts.value = UiState.Loading
            when (val result = hashtagRepository.getPostsByHashtag(hashtag)) {
                is DomainResult.Success -> {
                    nextCursor = result.data.second
                    _posts.value = UiState.Success(result.data.first)
                }
                is DomainResult.Error -> {
                    _posts.value = UiState.Error(result.message)
                }
                else -> {}
            }
        }
    }

    fun loadMore() {
        if (isLoadingMore || nextCursor == null) return
        val generation = loadGeneration
        val cursor = nextCursor
        isLoadingMore = true
        viewModelScope.launch {
            when (val result = hashtagRepository.getPostsByHashtag(hashtag, cursor = cursor)) {
                is DomainResult.Success -> {
                    // Drop the page if a refresh happened while we were fetching.
                    if (generation == loadGeneration) {
                        val existing = (_posts.value as? UiState.Success)?.data ?: emptyList()
                        nextCursor = result.data.second
                        _posts.value = UiState.Success(existing + result.data.first)
                    }
                }
                is DomainResult.Error -> { /* keep existing data */ }
                else -> {}
            }
            isLoadingMore = false
        }
    }
}
